import React, { forwardRef } from "react";

type NativeDivProps = React.HTMLAttributes<HTMLDivElement>;
type NativeSectionProps = React.HTMLAttributes<HTMLElement>;

export interface LayoutUIProps extends NativeDivProps {
  hasSider?: boolean;
}

export type LayoutHeaderProps = NativeSectionProps;
export type LayoutContentProps = NativeSectionProps;
export type LayoutFooterProps = NativeSectionProps;

export interface LayoutSiderProps extends NativeSectionProps {
  width?: number | string;
  collapsedWidth?: number | string;
  collapsed?: boolean;
  collapsible?: boolean;
  trigger?: React.ReactNode;
  breakpoint?: string;
  reverseArrow?: boolean;
  onCollapse?: (collapsed: boolean, type?: string) => void;
}

const joinClassNames = (...classNames: Array<string | undefined>) => {
  return classNames.filter(Boolean).join(" ") || undefined;
};

const toCssSize = (value: number | string | undefined) => {
  return typeof value === "number" ? `${value}px` : value;
};

export const LayoutHeader = forwardRef<HTMLElement, LayoutHeaderProps>(
  ({ className, style, ...restProps }, ref) => {
    return (
      <header
        ref={ref}
        className={joinClassNames("web2-layout-header", className)}
        style={{ flex: "0 0 auto", ...style }}
        {...restProps}
      />
    );
  }
);

export const LayoutContent = forwardRef<HTMLElement, LayoutContentProps>(
  ({ className, style, ...restProps }, ref) => {
    return (
      <main
        ref={ref}
        className={joinClassNames("web2-layout-content", className)}
        style={{ flex: "auto", minWidth: 0, ...style }}
        {...restProps}
      />
    );
  }
);

export const LayoutFooter = forwardRef<HTMLElement, LayoutFooterProps>(
  ({ className, style, ...restProps }, ref) => {
    return (
      <footer
        ref={ref}
        className={joinClassNames("web2-layout-footer", className)}
        style={{ flex: "0 0 auto", ...style }}
        {...restProps}
      />
    );
  }
);

export const LayoutSider = forwardRef<HTMLElement, LayoutSiderProps>(
  (
    {
      className,
      style,
      width = 200,
      collapsedWidth = 0,
      collapsed = false,
      collapsible: _collapsible,
      trigger: _trigger,
      breakpoint: _breakpoint,
      reverseArrow: _reverseArrow,
      onCollapse: _onCollapse,
      ...restProps
    },
    ref
  ) => {
    const siderWidth = toCssSize(collapsed ? collapsedWidth : width);

    return (
      <aside
        ref={ref}
        className={joinClassNames("web2-layout-sider", className)}
        style={{
          flex: `0 0 ${siderWidth}`,
          flexBasis: siderWidth,
          maxWidth: siderWidth,
          minWidth: siderWidth,
          width: siderWidth,
          ...style,
        }}
        {...restProps}
      />
    );
  }
);

interface LayoutUIComponent
  extends React.ForwardRefExoticComponent<
    LayoutUIProps & React.RefAttributes<HTMLDivElement>
  > {
  Header: typeof LayoutHeader;
  Content: typeof LayoutContent;
  Footer: typeof LayoutFooter;
  Sider: typeof LayoutSider;
}

const LayoutUI = forwardRef<HTMLDivElement, LayoutUIProps>(
  ({ className, style, hasSider = false, ...restProps }, ref) => {
    return (
      <div
        ref={ref}
        className={joinClassNames(
          "web2-layout",
          hasSider ? "web2-layout-has-sider" : undefined,
          className
        )}
        style={{
          boxSizing: "border-box",
          display: "flex",
          flex: "auto",
          flexDirection: hasSider ? "row" : "column",
          minHeight: 0,
          minWidth: 0,
          ...style,
        }}
        {...restProps}
      />
    );
  }
) as LayoutUIComponent;

LayoutUI.Header = LayoutHeader;
LayoutUI.Content = LayoutContent;
LayoutUI.Footer = LayoutFooter;
LayoutUI.Sider = LayoutSider;

export default LayoutUI;
